/* eslint-disable no-nested-ternary */
import React, { memo } from 'react';
import { withStyles, WithStyles } from '@material-ui/core/styles';
import { useIntl } from 'react-intl';
import AddRoundedIcon from '@material-ui/icons/AddRounded';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import styles from './manageVideoContent.styles';
import { Typography } from '../../components/Typography';
import IconOnlyButton from '../../components/Button/IconOnlyButton';
import { Button } from '../../components/Button';
import Table from '../../components/Table/Table';
import { formatFileSize } from '../../helper/localStorage';
import { TrashIcon } from '../../assets/icons';

interface IManageVideoContentView extends WithStyles<typeof styles> {
  uploadedVideoStreams: Array<any>;
  inProgressVideoUploads: Array<any>;
  addVideoStream: () => void;
  removeVideoStream: (name: string) => void;
  playVideoStream: (name: string) => void;
}

const ManageVideoContentView: React.FC<IManageVideoContentView> = ({
  classes,
  uploadedVideoStreams,
  inProgressVideoUploads,
  addVideoStream,
  removeVideoStream,
  playVideoStream,
}: IManageVideoContentView) => {
  const intl = useIntl();

  const uploadsColumns = [
    { name: 'filename', label: 'File Name' },
    { name: 'description', label: 'Description' },
    { name: 'date', label: 'Upload Date' },
    { name: 'size', label: 'Size' },
    {
      name: 'actions',
      label: 'Actions',
      options: {
        customBodyRender: (value, tableMeta, updateValue) =>
          tableMeta.rowData[2] === 'In Progress' ? (
            <>
              <IconOnlyButton
                aria-label="delete"
                onClick={() => removeVideoStream(tableMeta.rowData[0])}
                style={{ border: 'none' }}
              >
                <img src={TrashIcon} alt="Remove" />
              </IconOnlyButton>
            </>
          ) : (
            <>
              <IconOnlyButton
                aria-label="delete"
                onClick={() => removeVideoStream(tableMeta.rowData[0])}
                style={{ border: 'none' }}
              >
                <img src={TrashIcon} alt="Remove" />
              </IconOnlyButton>
              <IconOnlyButton
                aria-label="play"
                onClick={() => playVideoStream(tableMeta.rowData[0])}
                style={{ border: 'none' }}
              >
                <PlayArrowIcon />
              </IconOnlyButton>
            </>
          ),
      },
    },
  ];

  let uploadsData = inProgressVideoUploads.map((e) => [
    e.stream.name,
    e.stream.description,
    'In Progress',
    `${formatFileSize(e.uploadedBytes)} of ${formatFileSize(
      e.stream.file.size
    )}`,
  ]);

  uploadsData = uploadsData.concat(
    uploadedVideoStreams.map((e) => [
      e.filename,
      e.description,
      new Date(e.date).toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }),
      formatFileSize(e.size),
    ])
  );

  return (
    <div className={classes.root}>
      <div className={classes.topWrapper}>
        <Typography variant="h3">
          {intl.formatMessage({
            id: 'manage_video_content',
          })}
        </Typography>
      </div>

      <div className={classes.topButtons}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddRoundedIcon />}
          label={intl.formatMessage({
            id: 'manage_video_content_upload_stream',
          })}
          onClick={() => addVideoStream()}
        />
      </div>

      <div className={classes.bottomWrapper}>
        {uploadsData.length <= 0 ? (
          <Typography variant="h3">
            {intl.formatMessage({
              id: 'manage_video_content_no_uploads',
            })}
          </Typography>
        ) : (
          <Table
            id="video-streams-table"
            columns={uploadsColumns}
            data={uploadsData}
            title={intl.formatMessage({
              id: 'manage_video_content_uploaded_streams',
            })}
            pagination
          />
        )}
      </div>
    </div>
  );
};

export default memo(withStyles(styles)(ManageVideoContentView));
